import type { RichTextEditorSerializeAs } from "../RichTextEditorProps";

const INDENT = "  ";
const BLOCK_TAGS: ReadonlySet<string> = new Set([
  "blockquote",
  "div",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "li",
  "ol",
  "p",
  "pre",
  "table",
  "tbody",
  "td",
  "th",
  "thead",
  "tr",
  "ul",
]);

const formatJson = (value: string): string => {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const isBlockElement = (node: Node): node is Element => {
  return node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has((node as Element).tagName.toLowerCase());
};

const hasBlockChild = (element: Element): boolean => {
  return Array.from(element.children).some(isBlockElement);
};

const openTag = (element: Element): string => {
  const attrs = Array.from(element.attributes)
    .map((attr) => ` ${attr.name}="${attr.value}"`)
    .join("");
  return `<${element.tagName.toLowerCase()}${attrs}>`;
};

const closeTag = (element: Element): string => `</${element.tagName.toLowerCase()}>`;

const inlineMarkup = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";
  if (node.nodeType === Node.ELEMENT_NODE) return (node as Element).outerHTML;
  return "";
};

const formatElement = (element: Element, depth: number, lines: string[]): void => {
  const pad = INDENT.repeat(depth);
  if (!hasBlockChild(element) || element.tagName.toLowerCase() === "pre") {
    const inner = element.innerHTML.trim();
    lines.push(`${pad}${openTag(element)}${inner}${closeTag(element)}`);
    return;
  }
  lines.push(`${pad}${openTag(element)}`);
  for (const child of Array.from(element.childNodes)) {
    if (isBlockElement(child)) {
      formatElement(child, depth + 1, lines);
    } else {
      const inline = inlineMarkup(child).trim();
      if (inline) lines.push(`${INDENT.repeat(depth + 1)}${inline}`);
    }
  }
  lines.push(`${pad}${closeTag(element)}`);
};

const formatHtml = (value: string): string => {
  if (typeof DOMParser === "undefined") return value;
  try {
    const doc = new DOMParser().parseFromString(`<body>${value}</body>`, "text/html");
    const lines: string[] = [];
    for (const node of Array.from(doc.body.childNodes)) {
      if (isBlockElement(node)) {
        formatElement(node, 0, lines);
      } else {
        const inline = inlineMarkup(node).trim();
        if (inline) lines.push(inline);
      }
    }
    const formatted = lines.join("\n");
    return formatted.length > 0 ? formatted : value;
  } catch {
    return value;
  }
};

export const formatSerializedOutput = (value: string, serializeAs: RichTextEditorSerializeAs): string => {
  switch (serializeAs) {
    case "components":
    case "lexical-json":
      return formatJson(value);
    case "html":
      return formatHtml(value);
    case "markdown":
    default:
      return value;
  }
};
